import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";
import { LoadingSpinner } from "./components/UI";
import Home from "./components/Home";
import Features from "./components/Features";
import Pricing from "./components/Pricing";
import Blog from "./components/Blog";
import ContactUs from "./components/ContactUs";
import About from "./components/About";
import Careers from "./components/Careers";
import Login from "./components/Login";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";
import DeleteAccount from "./components/DeleteAccount";
import DashboardRouter from "./components/Dashboard/DashboardRouter";
import LeaveManagement from "./components/Dashboard/LeaveManagement";
import PayrollManagement from "./components/Dashboard/PayrollManagement";
import ProtectedRoute from "./components/Navigation/ProtectedRoute";
import AuthenticatedNav from "./components/Navigation/AuthenticatedNav";
import CandidateLogin from "./components/CandidatePortal/CandidateLogin";
import CandidateOfferView from "./components/CandidatePortal/CandidateOfferView";
import WhatsAppFloat from "./components/WhatsAppFloat";
import NotFound from "./components/NotFound";
import BlogPost from "./components/BlogPost";
import SolutionPage from "./components/SolutionPage";
import { SOLUTIONS } from "./content/solutions";

const serif = { fontFamily: '"DM Serif Display", Georgia, serif' };
const ACCENT = '#1a5fff';

const NAV_LINKS = [
  { to: '/', label: 'Home', exact: true },
  { to: '/features', label: 'Features' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact', label: 'Contact' },
];

// Start each new page at the top; for /page#section links, scroll to that section.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

function AppContent() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isDashboardRoute = location.pathname.startsWith('/dashboard');

  const isActive = (to, exact) => exact ? location.pathname === to : location.pathname.startsWith(to);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f0f4ff' }}>
        <div className="text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <LoadingSpinner size="xl" />
          <p style={{ color: '#717888', fontWeight: 500 }}>Loading DigiHR…</p>
        </div>
      </div>
    );
  }

  if (isDashboardRoute) {
    return (
      <Routes>
        <Route path="/dashboard/*" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />
      </Routes>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', fontFamily: '"DM Sans",system-ui,sans-serif' }}>
      {/* NAV */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(255,255,255,.92)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #e4e7ed',
        padding: '0 5%',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: 64,
      }}>
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', ...serif, fontSize: 22, color: '#0d1117', letterSpacing: '-.3px' }}
          onClick={() => setMobileOpen(false)}>
          DigiHR<span style={{ width: 8, height: 8, borderRadius: '50%', background: ACCENT, display: 'inline-block' }} />
        </Link>

        {/* Desktop links */}
        <ul style={{ display: 'flex', alignItems: 'center', gap: 6, listStyle: 'none', margin: 0, padding: 0 }} className="nav-links-desktop">
          {NAV_LINKS.map(({ to, label, exact }) => (
            <li key={to}>
              <Link to={to} style={{
                fontSize: 14, fontWeight: isActive(to, exact) ? 500 : 400,
                color: isActive(to, exact) ? '#0d1117' : '#3a3f4a',
                textDecoration: 'none', padding: '6px 14px', borderRadius: 6,
                background: isActive(to, exact) ? '#f6f7f9' : 'transparent',
                transition: 'all .18s ease', display: 'block'
              }}
                onMouseEnter={e => { if (!isActive(to, exact)) { e.currentTarget.style.background = '#f6f7f9'; e.currentTarget.style.color = '#0d1117'; } }}
                onMouseLeave={e => { if (!isActive(to, exact)) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#3a3f4a'; } }}>
                {label}
              </Link>
            </li>
          ))}
        </ul>

        {/* CTAs */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }} className="nav-cta-desktop">
          {isAuthenticated ? (
            <AuthenticatedNav user={user} />
          ) : (
            <>
              <Link to="/login" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 12, background: 'transparent', color: '#3a3f4a', border: '1px solid #e4e7ed', fontSize: 14, fontWeight: 500, textDecoration: 'none', transition: 'all .2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f6f7f9'; e.currentTarget.style.borderColor = '#c8cdd8'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#e4e7ed'; }}>
                Log in
              </Link>
              <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 12, background: ACCENT, color: '#fff', fontSize: 14, fontWeight: 500, textDecoration: 'none', boxShadow: '0 2px 8px rgba(26,95,255,.3)', transition: 'all .2s' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1248d6'; }}
                onMouseLeave={e => { e.currentTarget.style.background = ACCENT; }}>
                Book a free Demo
              </Link>
            </>
          )}
          {/* Mobile hamburger */}
          <button onClick={() => setMobileOpen(o => !o)} className="nav-hamburger"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen}
            style={{ display: 'none', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', width: 44, height: 44, padding: 0 }}>
            {[0,1,2].map(i => <span key={i} style={{ width: 22, height: 2, background: '#3a3f4a', borderRadius: 2, display: 'block' }} />)}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="nav-mobile-menu" style={{ background: '#fff', borderBottom: '1px solid #e4e7ed', padding: '12px 5%', display: 'none' }}>
          {NAV_LINKS.map(({ to, label }) => (
            <Link key={to} to={to} onClick={() => setMobileOpen(false)}
              style={{ display: 'block', padding: '10px 0', fontSize: 15, color: '#3a3f4a', textDecoration: 'none', borderBottom: '1px solid #f6f7f9' }}>
              {label}
            </Link>
          ))}
          {!isAuthenticated && (
            <Link to="/login" onClick={() => setMobileOpen(false)}
              style={{ display: 'block', marginTop: 12, padding: '12px 0', textAlign: 'center', background: 'transparent', color: '#3a3f4a', border: '1px solid #e4e7ed', borderRadius: 12, fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>
              Log in
            </Link>
          )}
          {!isAuthenticated && (
            <Link to="/contact" onClick={() => setMobileOpen(false)}
              style={{ display: 'block', marginTop: 12, padding: '12px 0', textAlign: 'center', background: ACCENT, color: '#fff', borderRadius: 12, fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>
              Book a free Demo
            </Link>
          )}
        </div>
      )}

      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {SOLUTIONS.map(({ slug }) => <Route key={slug} path={`/${slug}`} element={<SolutionPage slug={slug} />} />)}
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/leaves" element={<ProtectedRoute><LeaveManagement /></ProtectedRoute>} />
          <Route path="/payroll" element={<ProtectedRoute><PayrollManagement /></ProtectedRoute>} />
          <Route path="/candidate-portal/login" element={<CandidateLogin />} />
          <Route path="/candidate-portal" element={<CandidateOfferView />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <WhatsAppFloat />

      <style>{`
        @media(max-width:900px){
          .nav-links-desktop{ display:none !important; }
          .nav-hamburger{ display:flex !important; }
          .nav-mobile-menu{ display:block !important; }
          .nav-cta-desktop a:first-child{ display:none !important; }
        }
      `}</style>
    </div>
  );
}

export default App;
